import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, RotateCcw, Download, Bot, PencilLine, History, Globe, CopyPlus } from "lucide-react";
import { api, errMsg, downloadBlob } from "../../lib/api";
import { timeAgo } from "../../lib/streamParse";

const SRC = { ai: [Bot, "AI"], manual: [PencilLine, "Edited"], restore: [History, "Restored"], remix: [CopyPlus, "Copied"] };

export const VersionsPane = ({ project, onPreview, onRestored, onCopy, refreshKey }) => {
  const [rows, setRows] = useState(null);

  useEffect(() => { api.versions(project.id).then(setRows).catch(() => setRows([])); }, [project.id, refreshKey]);

  const restore = async (v) => {
    try {
      const nv = await api.restoreVersion(project.id, v.id);
      toast.success(`Restored version ${v.number} as version ${nv.number}`);
      onRestored();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const download = async (v) => {
    try { downloadBlob(await api.exportZip(project.id, v.id), `v${v.number}.zip`); } catch (e) { toast.error("Download failed"); }
  };

  if (rows === null) return <div className="p-6 space-y-3">{[0, 1, 2].map((i) => <div key={i} className="h-16 rounded-xl shimmer border border-slate-200" />)}</div>;
  if (!rows.length) return <div className="h-full flex items-center justify-center text-sm text-slate-500" data-testid="versions-empty">No versions yet.</div>;

  return (
    <div className="h-full overflow-y-auto thin-scroll p-4 sm:p-6" data-testid="versions-pane">
      <ol className="relative border-l border-slate-200 ml-2 space-y-4">
        {rows.map((v) => {
          const [Icon, label] = SRC[v.source] || SRC.ai;
          const current = v.id === project.current_version_id;
          const live = v.id === project.published_version_id;
          return (
            <li key={v.id} className="ml-5" data-testid={`version-row-${v.number}`}>
              <span className={`absolute -left-[7px] mt-4 w-3.5 h-3.5 rounded-full border-2 ${current ? "bg-emerald-500 border-emerald-300" : "bg-slate-100 border-slate-300"}`} />
              <div className="rounded-xl border border-slate-200 bg-white p-4 hover:border-slate-300 transition-colors">
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="font-mono font-semibold text-slate-900">v{v.number}</span>
                  <span className="flex items-center gap-1 text-slate-500"><Icon size={11} /> {label}</span>
                  {current && <span className="px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-700 font-mono text-[10px]">Current</span>}
                  {live && <span className="px-1.5 py-0.5 rounded bg-indigo-50 border border-indigo-200 text-indigo-600 font-mono text-[10px] flex items-center gap-1"><Globe size={9} /> Live</span>}
                  <span className="ml-auto text-slate-400">{timeAgo(v.created_at)} · {v.file_count} files</span>
                </div>
                <p className="mt-2 text-sm text-slate-600 line-clamp-2">{v.label}</p>
                <div className="mt-3 flex gap-2">
                  <button onClick={() => onPreview(v.id)} data-testid={`version-preview-${v.number}`} className="h-7 px-2.5 rounded-md border border-slate-300 text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1.5"><Eye size={12} /> Preview</button>
                  {!current && <button onClick={() => restore(v)} data-testid={`version-restore-${v.number}`} className="h-7 px-2.5 rounded-md border border-slate-300 text-xs text-slate-600 hover:text-slate-900 hover:border-sky-300 flex items-center gap-1.5"><RotateCcw size={12} /> Restore</button>}
                  <button onClick={() => onCopy(v)} data-testid={`version-copy-${v.number}`} title="Start a new project from this version" className="h-7 px-2.5 rounded-md border border-slate-300 text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1.5"><CopyPlus size={12} /> Copy</button>
                  <button onClick={() => download(v)} data-testid={`version-download-${v.number}`} className="h-7 px-2.5 rounded-md border border-slate-300 text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1.5"><Download size={12} /> ZIP</button>
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default VersionsPane;
